import {
  AttributeValue,
  EditedAttributeValues,
  FormTypes,
  SectionInstance,
} from '../models/section-definition.model';
import {
  AttributeValidationError,
  getNumberValidationError,
} from '../components/common/section-instance/attribute-rows/attribute-number-edit-row/attribute-number-edit-row.component';
import { getTextValidationError } from '../components/common/section-instance/attribute-rows/attribute-text-edit-row/attribute-text-edit-row.component';
import {
  getDateValidationError,
  isSameDate,
} from '../components/common/section-instance/attribute-rows/attribute-date-edit-row/attribute-date-edit-row.component';
import { getImageValidationError } from '../components/common/section-instance/attribute-rows/attribute-image-edit-row/attribute-image-edit-row.component';
import {
  getDropdownValidationError,
  isSameOptionIds,
} from '../components/common/section-instance/attribute-rows/attribute-dropdown-edit-row/attribute-dropdown-edit-row.component';

export interface UpdateAttributeValue {
  attributeDefinitionId: number;
  doubleValue?: number | null;
  stringValue?: string | null;
  dateValue?: string | null;
  urlValue?: string | null;
  selectedOptionIds?: number[];
}

export interface AttributeEdit {
  changed: UpdateAttributeValue[];
  invalid: boolean;
}

type EditedValue = number | string | number[] | null;

const editableTypes: string[] = [
  FormTypes.Number,
  FormTypes.Text,
  FormTypes.DateTime,
  FormTypes.Image,
  FormTypes.Dropdown,
].map((type) => FormTypes[type]);

// Collects the changed editable attributes of the given sections and whether any of them is invalid.
export function getAttributeEdit(
  sections: SectionInstance[],
  editableAttributeIds: number[],
  edited: EditedAttributeValues,
): AttributeEdit {
  const editable = sections
    .flatMap((section) => section.attributeValues)
    .filter(
      (attr) =>
        editableAttributeIds.includes(attr.attributeDefinitionId) &&
        editableTypes.includes(attr.type as string),
    );
  const changed = editable.filter((attr) => isAttributeChanged(attr, edited));
  const invalid = editable.some(
    (attr) =>
      (changed.includes(attr) || isEmpty(getCurrentValue(attr, edited))) &&
      !!getAttributeValidationError(attr, edited),
  );
  return { changed: changed.map((attr) => toUpdateAttributeValue(attr, edited)), invalid };
}

function getOriginalValue(attr: AttributeValue): EditedValue {
  switch (attr.type as string) {
    case FormTypes[FormTypes.Text]:
    case FormTypes[FormTypes.Image]:
      return attr.stringValue || null;
    case FormTypes[FormTypes.DateTime]:
      return attr.dateValue ?? null;
    case FormTypes[FormTypes.Dropdown]:
      return attr.selectedOptionIds ?? [];
    default:
      return attr.doubleValue ?? null;
  }
}

function getCurrentValue(attr: AttributeValue, edited: EditedAttributeValues): EditedValue {
  return attr.attributeDefinitionId in edited
    ? edited[attr.attributeDefinitionId]
    : getOriginalValue(attr);
}

function isEmpty(value: EditedValue): boolean {
  return value == null || value === '' || (Array.isArray(value) && value.length === 0);
}

function isAttributeChanged(attr: AttributeValue, edited: EditedAttributeValues): boolean {
  if (!(attr.attributeDefinitionId in edited)) return false;

  const value = edited[attr.attributeDefinitionId];
  const original = getOriginalValue(attr);
  switch (attr.type as string) {
    case FormTypes[FormTypes.DateTime]:
      return !isSameDate(value as string | null, original as string | null);
    case FormTypes[FormTypes.Dropdown]:
      return !isSameOptionIds(value as number[], original as number[]);
    default:
      return value !== original;
  }
}

function getAttributeValidationError(attr: AttributeValue, edited: EditedAttributeValues): AttributeValidationError | null {
  const value = getCurrentValue(attr, edited);
  switch (attr.type as string) {
    case FormTypes[FormTypes.Text]:
      return getTextValidationError(attr, value as string | null);
    case FormTypes[FormTypes.DateTime]:
      return getDateValidationError(attr, value as string | null);
    case FormTypes[FormTypes.Image]:
      return getImageValidationError(attr, value as string | null);
    case FormTypes[FormTypes.Dropdown]:
      return getDropdownValidationError(attr, value as number[]);
    default:
      return getNumberValidationError(attr, value as number | null);
  }
}

function toUpdateAttributeValue(attr: AttributeValue, edited: EditedAttributeValues): UpdateAttributeValue {
  const attributeDefinitionId = attr.attributeDefinitionId;
  const value = edited[attributeDefinitionId];
  switch (attr.type as string) {
    case FormTypes[FormTypes.Text]:
      return { attributeDefinitionId, stringValue: value as string | null };
    case FormTypes[FormTypes.DateTime]:
      return { attributeDefinitionId, dateValue: value as string | null };
    case FormTypes[FormTypes.Image]:
      return { attributeDefinitionId, urlValue: value as string | null };
    case FormTypes[FormTypes.Dropdown]:
      return { attributeDefinitionId, selectedOptionIds: value as number[] };
    default:
      return { attributeDefinitionId, doubleValue: value as number | null };
  }
}
